import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Sparkles, Lock, User } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PortalLogin = () => {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // If already logged in, redirect to the correct dashboard
  useEffect(() => {
    const token = localStorage.getItem('portal_token');
    if (!token) return;
    axios
      .get(`${BACKEND_URL}/api/portal/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        if (res.data.role === 'admin') navigate('/admin', { replace: true });
        else if (res.data.role === 'user_admin') navigate('/dashboard', { replace: true });
      })
      .catch(() => {
        localStorage.removeItem('portal_token');
      });
  }, [navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/portal/login`, {
        username: username.trim(),
        password,
      });
      localStorage.setItem('portal_token', res.data.token);
      localStorage.setItem('portal_role', res.data.role);
      localStorage.setItem('portal_slug', res.data.slug || '');
      localStorage.setItem('portal_username', res.data.username || '');
      // Legacy admin token key so existing AdminPanel keeps working
      if (res.data.role === 'admin') {
        localStorage.setItem('admin_token', res.data.token);
        navigate('/admin', { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Yanlış giriş məlumatları');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'radial-gradient(ellipse at top, rgba(88, 28, 135, 0.4) 0%, rgba(15, 10, 30, 1) 60%)',
      padding: 20,
    }}>
      <div style={{
        maxWidth: 420,
        width: '100%',
        background: 'rgba(20, 15, 40, 0.6)',
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(251, 191, 36, 0.25)',
        borderRadius: 16,
        padding: 36,
        boxShadow: '0 30px 80px rgba(0,0,0,0.7), 0 0 60px rgba(251,191,36,0.1)',
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 24 }}>
          <div style={{
            width: 60, height: 60, borderRadius: '50%',
            background: 'radial-gradient(circle, #fbbf24, #f59e0b)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 0 40px rgba(251,191,36,0.5)', marginBottom: 12,
          }}>
            <Sparkles size={28} color="#0b0b0f" />
          </div>
          <h1 style={{
            fontFamily: "'Cinzel', serif",
            fontSize: 26, fontWeight: 800, color: '#fbbf24',
            marginBottom: 4, textAlign: 'center',
          }}>Portal Girişi</h1>
          <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.6)', textAlign: 'center' }}>
            Öz kabinetinizə daxil olun
          </p>
        </div>

        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)' }}>İstifadəçi adı</span>
            <div style={{ position: 'relative' }}>
              <User size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.4)' }} />
              <input
                data-testid="portal-username"
                type="text"
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                style={{
                  width: '100%',
                  padding: '10px 12px 10px 36px',
                  background: 'rgba(0,0,0,0.4)',
                  border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: 8,
                  color: 'white',
                  fontSize: 14,
                  outline: 'none',
                }}
              />
            </div>
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)' }}>Parol</span>
            <div style={{ position: 'relative' }}>
              <Lock size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.4)' }} />
              <input
                data-testid="portal-password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                style={{
                  width: '100%',
                  padding: '10px 12px 10px 36px',
                  background: 'rgba(0,0,0,0.4)',
                  border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: 8,
                  color: 'white',
                  fontSize: 14,
                  outline: 'none',
                }}
              />
            </div>
          </label>
          {error && (
            <div data-testid="portal-error" style={{
              padding: 10,
              background: 'rgba(220,38,38,0.15)',
              border: '1px solid rgba(220,38,38,0.3)',
              borderRadius: 8,
              color: '#fca5a5',
              fontSize: 13,
              textAlign: 'center',
            }}>{error}</div>
          )}
          <button
            data-testid="portal-submit"
            type="submit"
            disabled={loading}
            style={{
              padding: '12px 20px',
              background: loading ? 'rgba(251,191,36,0.4)' : 'linear-gradient(135deg, #fbbf24, #f59e0b)',
              color: '#0b0b0f',
              border: 'none',
              borderRadius: 8,
              fontSize: 15,
              fontWeight: 700,
              cursor: loading ? 'not-allowed' : 'pointer',
              marginTop: 6,
              fontFamily: "'Cinzel', serif",
              letterSpacing: 0.5,
              boxShadow: '0 4px 15px rgba(251,191,36,0.35)',
            }}
          >
            {loading ? 'Yoxlanılır…' : 'Daxil ol'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default PortalLogin;
